/**
 * Minimal, dependency-free unified line-diff for the CLI — renders Edit/Write/MultiEdit
 * changes as +/- lines with limited context. Colorizers are injected so all TTY-gating
 * stays in cli.ts. Pure functions: equally usable in tests or a browser build.
 */
export interface DiffStyle {
  add?: (s: string) => string; // colorizer for added (+) lines
  del?: (s: string) => string; // colorizer for removed (-) lines
  dim?: (s: string) => string; // colorizer for context / hunk separators
}
export interface DiffOptions extends DiffStyle {
  context?: number;  // unchanged lines kept around each change (default 3)
  maxLines?: number; // cap rendered lines (default 160)
}

export type DiffOp = { t: ' ' | '+' | '-'; line: string };

/** LCS-based line diff → an ordered op list. O(n·m); guard very large inputs upstream. */
export function diffLines(before: string, after: string): DiffOp[] {
  const a = before ? before.split('\n') : [];
  const b = after ? after.split('\n') : [];
  const n = a.length, m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const ops: DiffOp[] = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { ops.push({ t: ' ', line: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { ops.push({ t: '-', line: a[i] }); i++; }
    else { ops.push({ t: '+', line: b[j] }); j++; }
  }
  while (i < n) ops.push({ t: '-', line: a[i++] });
  while (j < m) ops.push({ t: '+', line: b[j++] });
  return ops;
}

/** Count added / removed lines from a precomputed op list — for the `+N -M` header. */
export function statOf(ops: DiffOp[]): { added: number; removed: number } {
  let added = 0, removed = 0;
  for (const op of ops) { if (op.t === '+') added++; else if (op.t === '-') removed++; }
  return { added, removed };
}

/** Convenience: count added / removed lines between two strings (runs the diff). */
export function diffStat(before: string, after: string): { added: number; removed: number } {
  return statOf(diffLines(before, after));
}

/** Convenience: diff two strings and render. Returns '' if nothing changed. */
export function renderDiff(before: string, after: string, opts: DiffOptions = {}): string {
  if (before === after) return '';
  return formatDiff(diffLines(before, after), opts);
}

/** Render a precomputed op list as a compact, context-limited, capped unified diff. */
export function formatDiff(ops: DiffOp[], opts: DiffOptions = {}): string {
  const { context = 3, maxLines = 160 } = opts;
  const id = (s: string) => s;
  const add = opts.add ?? id, del = opts.del ?? id, dim = opts.dim ?? id;
  const keep = new Array(ops.length).fill(false);
  ops.forEach((op, k) => {
    if (op.t === ' ') return;
    for (let d = -context; d <= context; d++) if (k + d >= 0 && k + d < ops.length) keep[k + d] = true;
  });
  const out: string[] = [];
  let elided = false;
  for (let k = 0; k < ops.length; k++) {
    if (!keep[k]) { if (!elided && out.length) { out.push(dim('  ⋯')); elided = true; } continue; }
    elided = false;
    const op = ops[k];
    out.push(op.t === '+' ? add('  +' + op.line) : op.t === '-' ? del('  -' + op.line) : dim('   ' + op.line));
    if (out.length >= maxLines) { out.push(dim('  … diff truncated')); break; }
  }
  return out.join('\n');
}
